import { useEffect } from 'react';
import { useAuthSync } from './useAuthSync';
import usePreferencesStore from '../store/usePreferencesStore';

/**
 * Custom hook to manage user preferences using Zustand store
 * @returns {Object} Object containing preferences, actions, and loading state
 */
export const useUserPreferences = () => {
  const { isAuthenticated, cognitoAuth } = useAuthSync();
  const {
    preferences,
    isLoading,
    error,
    updatePreferences,
    clearError
  } = usePreferencesStore();

  // Auto-fetch preferences when user is authenticated
  useEffect(() => {
    if (isAuthenticated && cognitoAuth.user?.access_token) {
      // Call fetchPreferences directly from the store to avoid dependency issues
      usePreferencesStore.getState().fetchPreferences(cognitoAuth.user.access_token);
    }
  }, [isAuthenticated, cognitoAuth.user?.access_token]);

  // Wrapper function for updating preferences that includes auth check
  const handleUpdatePreferences = async (newPreferences) => {
    if (!isAuthenticated || !cognitoAuth.user?.access_token) {
      console.error('User not authenticated');
      return false;
    }

    return await updatePreferences(newPreferences, cognitoAuth.user.access_token);
  };

  // Wrapper function for refreshing preferences
  const refreshPreferences = async () => {
    if (!isAuthenticated || !cognitoAuth.user?.access_token) {
      console.error('User not authenticated');
      return null;
    }

    return await usePreferencesStore.getState().refreshPreferences(cognitoAuth.user.access_token);
  };

  return {
    preferences,
    isLoading,
    error,
    updatePreferences: handleUpdatePreferences,
    refreshPreferences,
    clearError
  };
};

export default useUserPreferences;
